import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Product } from '~shared/models';
import { PriceTypes } from '~shared/modules/price-type-toggler/models/price-types.enum';
import { ApplicationEventService } from '~shared/modules/application-event/services/application-event.service';
import {
  CarouselButtonApplicationEvent,
  CarouselSwipeApplicationEvent,
  ProductClickApplicationEvent,
  SelectItemApplicationEventModel,
} from '~shared/modules/application-event/models';
import { NavigationHelper } from '~shared/services/navigation-helper.service';
import { ProductsService } from '~app/modules/storyblok/services/products.service';
import { UrlUtil } from '~shared/utils/url.util';
import { MediaObserver } from '@angular/flex-layout';
import { MEDIA_ALISES } from '~shared/constants/media.constant';
import { CarouselActionType } from '~shared/modules/application-event/constants';
import {
  GaTrackingEventAction,
  GaTrackingEventLabel,
  GaTrackingEventName,
} from '~shared/modules/ga-tracking/constants';
import { Direction, DirectionType } from '~shared/constants';
import { PageDetectionUtil } from '~shared/utils/page-detection.util';
import { UrlUtils } from '~app/modules/generic/utils/url.utils';
import {
  StoryBlokProductCarousel,
  RecommendedProductsCarousel,
  ProductCarouselTeaser,
} from '../../models/storyblok-content-product-carousel.model';
import { GtmDatalayerService } from '~shared/services/gtm-datalayer.service';

@Component({
  selector: 'app-content-product-carousel',
  templateUrl: './content-product-carousel.component.html',
  styleUrls: ['./content-product-carousel.component.scss'],
})
export class ContentProductCarouselComponent implements OnInit, OnDestroy {
  @Input() title?: string;
  @Input() products?: StoryBlokProductCarousel;
  @Input() limit?: number;
  @Input() recommendedProductsCarousel?: RecommendedProductsCarousel;
  @Input() border: boolean;
  @Input() teaser?: ProductCarouselTeaser[];

  get isContentLandingPage(): boolean {
    return PageDetectionUtil.isLandingPagePage(this.currentUrl);
  }

  private get isPDPPage(): boolean {
    return PageDetectionUtil.isProductDetailsPage(this.currentUrl);
  }

  storyBlokProducts: Product[];
  teaserWidth: string;
  ngDestroy$ = new Subject();
  isMobile = false;
  currentUrl = this.navigationHelper.getCurrentUrl();
  pageType = UrlUtil.getPageTypeFromCurrentUrl(this.currentUrl);
  DIRECTION = Direction;
  readonly largeTextLengthIndicator = 36;

  constructor(
    private productsService: ProductsService,
    private applicationEventService: ApplicationEventService,
    private navigationHelper: NavigationHelper,
    private mediaObserver: MediaObserver,
    private gtmDatalayerService: GtmDatalayerService
  ) {}

  ngOnInit(): void {
    this.initializeMediaObservable();
    this.loadProductsData();
  }

  ngOnDestroy(): void {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }

  private loadProductsData(): void {
    if (this.recommendedProductsCarousel) {
      this.loadRecommendedProductsData();
      return;
    }
    this.loadSortedProductsData();
  }

  private filterByStoryblokSelection(loadedProducts: Array<Product> = []): Array<Product> {
    const selectedProducts: string[] = this.products.selectedProducts || [];
    return loadedProducts.filter((product: Product) => selectedProducts.includes(product.id));
  }

  private filterOutPDPProduct(loadedProducts: Array<Product>): Array<Product> {
    if (!this.isPDPPage) {
      return loadedProducts;
    }

    // If PDP I check for the current product so it's not displayed in the carousel
    const currentPDPId = UrlUtils.getProductIdFromUrl(this.currentUrl);

    return loadedProducts.filter((product: Product) => product.id !== currentPDPId);
  }

  private loadSortedProductsData(): void {
    this.productsService
      .getSortedProducts({
        limit: this.limit,
        orderedIds: this.products.selectedProducts,
      })
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe((products) => {
        this.storyBlokProducts = this.filterByStoryblokSelection(this.filterOutPDPProduct(products.items));
      });
  }

  private loadRecommendedProductsData(): void {
    this.title = this.recommendedProductsCarousel.title;

    this.productsService
      .getProductsByEndpoint(this.recommendedProductsCarousel.endpoint)
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe((products) => {
        this.storyBlokProducts = this.filterOutPDPProduct(products.items);
      });
  }

  private initializeMediaObservable(): void {
    this.gtmDatalayerService.gtagEvent({
      label: GaTrackingEventLabel.PromoView,
      event: GaTrackingEventName.PromoView,
      action: GaTrackingEventAction.PromoView,
    });

    this.mediaObserver
      .asObservable()
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe(() => {
        this.isMobile = this.mediaObserver.isActive(MEDIA_ALISES.mobile);
      });
  }

  onProductClick(product: Product, priceType: PriceTypes, index: number): void {
    this.publishProductClickEvent(product, priceType, index);
    this.publishSelectItemEvent(product, priceType, index);
  }

  private publishProductClickEvent(product: Product, priceType: PriceTypes, index: number): void {
    this.applicationEventService.publish(
      new ProductClickApplicationEvent(this.currentUrl, product, priceType, this.pageType, false, index, '', '', true)
    );
  }

  private publishSelectItemEvent(product: Product, priceType: PriceTypes, productIndex: number): void {
    this.applicationEventService.publish(
      new SelectItemApplicationEventModel(
        UrlUtil.getPageTypeFromCurrentUrl(this.navigationHelper.getCurrentUrl()),
        product,
        priceType,
        productIndex,
        true
      )
    );
  }

  hasTeaser(): boolean {
    return this.teaser && this.teaser?.length > 0;
  }

  isLongText(): boolean {
    return this.teaser[0]?.text?.length > this.largeTextLengthIndicator || false;
  }

  onWidthChanged(width: number): string {
    return (this.teaserWidth = `${width}px`);
  }

  checkIfHasMoreThanFiveProducts(products: Product[]): boolean {
    return products?.length >= 5;
  }

  onProductCarouselNavigation(direction: DirectionType): void {
    this.applicationEventService.publish(
      new CarouselButtonApplicationEvent(
        CarouselActionType.ContentCarouselButton,
        direction === this.DIRECTION.RIGHT ? GaTrackingEventLabel.Right : GaTrackingEventLabel.Left
      )
    );
  }

  onProductCarouselSwipe(direction: DirectionType): void {
    this.applicationEventService.publish(
      new CarouselSwipeApplicationEvent(
        CarouselActionType.ContentCarouselSwipe,
        direction === this.DIRECTION.RIGHT ? GaTrackingEventLabel.RightSwipe : GaTrackingEventLabel.LeftSwipe
      )
    );
  }
}
